import React, { useState, useEffect, useMemo } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getMarketHistory } from "../../../common/services/markets/marketService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const MarketHistory = ({ marketId }) => {
  const { t } = useTranslation(["markets"]);
  const [marketHistory, setMarketHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  // Fetch market history on component mount
  useEffect(() => {
    const fetchHistory = async () => {
      const data = await getMarketHistory(marketId);
      const formattedData = mapToViewModel(data);
      setMarketHistory(_.orderBy(formattedData, "id", "desc"));
      setIsLoading(false);
    };

    fetchHistory();
  }, [marketId]);

  // Map API data to view model with formatted dates
  const mapToViewModel = (markets) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return markets.map((market) => ({
      id: market.id,
      name: market.name,
      venueName: market.venueName || "",
      productName: market.productName || "",
      termScheduleName: market.termScheduleName || "",
      active: market.active,
      updatedByUserAccountId: market.createdByUserAccountId,
      updatedBy:
        market.updatedByUserAccountFirstName +
        " " +
        market.updatedByUserAccountLastName,
      updatedDate: new Date(market.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(market.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: market.eventId,
    }));
  };

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("Record"),
        sortable: true,
        filter: true,
        sort: "desc",
        minWidth: 100,
      },
      {
        field: "name",
        headerName: t("Name"),
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        field: "venueName",
        headerName: t("Venue"),
        sortable: true,
        filter: true,
        minWidth: 120,
      },
      {
        field: "productName",
        headerName: t("Product"),
        sortable: true,
        filter: true,
        minWidth: 120,
      },
      {
        field: "termScheduleName",
        headerName: t("TermSchedule"),
        sortable: true,
        filter: true,
        minWidth: 140,
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
        minWidth: 160,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
        minWidth: 160,
      },
      {
        field: "eventId",
        headerName: t("EventId"),
        sortable: true,
        filter: true,
        minWidth: 120,
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      flex: 1,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("MarketID")} {marketId}
      </div>
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={marketHistory}
        getRowId={(params) => params.data.id.toString()}
        // Use normal layout instead of autoHeight for pagination
        domLayout="autoHeight"
        animateRows={true}
        // Enable pagination
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={paginationConfig.pageSizeOptions}
        onPaginationChanged={(event) => {
          const newPageSize = event.api.paginationGetPageSize();
          if (newPageSize !== pageSize) {
            handlePageSizeChange(newPageSize);
          }
        }}
        suppressPaginationPanel={false}
        height="400px"
        // Allow horizontal scrolling as this table has more columns
        allowHorizontalScroll={true}
      />
    </div>
  );
};

export default MarketHistory;

